/*
  1t1fbyeZ — My Studio composer clothing-item upload persists to GCS.

  BUG: the composer's "Your clothing item / Add photo" control (main-my-studio.liquid)
  held the chosen image client-side only (FileReader → data-URL preview) and never
  persisted it. After ordering, GCS had NO customers/<uid>/uploads/ object and the
  order line item carried no "Source image" property — so fulfillment had nothing to
  generate from (live E2E, order #1011, 2026-07-24).

  FIX: wire the composer upload through the SAME studio-backend signed-URL path the
  product-photo library (studio-uploads.js) and the showroom buy box
  (studio-precheckout-upload.js) already use:
      POST /signed-url → PUT to GCS (customers/<uid>/uploads/) → GET /uploads for the
      freshest signed display URL.
  Then stamp the order (line-item properties), byte-for-byte matching the showroom's
  DAKI5Sdf contract so the fulfillment identity bridge / resolveUploadRefs resolves it
  exactly like a library upload:
      properties[Source image]     = signed GET URL (human-clickable on the order)
      properties[Source image ref] = customers/<uid>/uploads/<file> (durable GCS key)

  Auth: reuses the global firebase-auth.js app via getApp() (studio-prod customer
  project) — the same instance studio-uploads.js observes on this page. Upload is
  Firebase-token gated (backend requires a verified email).

  This module owns ONLY the real upload + stamping. The composer's inline JS
  (main-my-studio.liquid) owns the modal, preview render and paste-URL fallback; the
  two talk via custom events on the .ed-studio root:
      in:  composer:upload-file   { file }
      out: composer:upload-done   { displayUrl, ref, fileName }
      out: composer:upload-error  { message, status }

  Defensive: every hook is guarded, so the module is a no-op on any page without the
  composer or before firebase inits.
*/
import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { authedFetch, renderStatus, vt } from '@studio/verify-email';

const root      = document.querySelector('.ed-studio');
const form      = root && root.querySelector('.ed-studio-split');
const dashboard = document.querySelector('[data-studio-dashboard]');

if (root && form && dashboard && getApps().length) {
  const BACKEND  = (dashboard.getAttribute('data-backend-url') || '').replace(/\/$/, '');
  const urlInput = form.querySelector('[data-source-image-url]');   // properties[Source image]
  const refInput = form.querySelector('[data-source-image-ref]');   // properties[Source image ref]
  const auth = getAuth(getApp());

  const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
  const MAX_BYTES = 15 * 1024 * 1024;
  let busy = false;

  function emitDone(detail) {
    root.dispatchEvent(new CustomEvent('composer:upload-done', { detail }));
  }
  // uGqPRiwp: `composer:upload-error` was dispatched and nothing anywhere in the
  // theme listened for it, so every failure on this control — including the
  // email_verified 403 that blocks the whole flow — was silently dropped. Render
  // it into the modal's own status line as well as emitting the event, and mount
  // the resend control when the failure is the verification gate.
  const statusEl = form.querySelector('[data-composer-upload-status]');

  // Every message on this line is a failure (success clears it), so any
  // non-empty message is an error. The writer itself is the shared one.
  const setStatus = (message, status) => renderStatus(statusEl, message, !!message, status);

  function emitError(message, status) {
    setStatus(message, status);
    root.dispatchEvent(new CustomEvent('composer:upload-error', { detail: { message, status } }));
  }

  async function api(path, opts) {
    // Token, Authorization header and the retry-once on the email_verified
    // gate: authedFetch (studio-verify-email.js).
    const res = await authedFetch(BACKEND + path, opts);
    if (!res.ok) {
      let msg = 'Upload failed (' + res.status + ')';
      if (res.status === 403) msg = vt('gate_uploads');
      if (res.status === 415) msg = 'That file type is not supported. Use JPG, PNG or WebP.';
      if (res.status === 429) msg = 'You have reached your upload limit.';
      const e = new Error(msg); e.status = res.status; throw e;
    }
    return res.status === 204 ? null : res.json();
  }

  // Identical upload contract to studio-precheckout-upload.js / studio-uploads.js.
  async function uploadOne(file) {
    // 1) scoped, size-bounded V4 signed PUT URL
    const signed = await api('/signed-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: file.type, size: file.size }),
    });
    // 2) PUT straight to GCS, echoing the exact signed headers (byte-for-byte
    //    contract: x-goog-content-length-range is baked into the V4 signature).
    const headers = Object.assign(
      { 'Content-Type': signed.contentType || file.type },
      signed.requiredHeaders || {}
    );
    const put = await fetch(signed.url, { method: 'PUT', headers, body: file });
    if (!put.ok) throw new Error('Upload failed (' + put.status + ')');

    // 3) durable ref = bucket-independent GCS object key (gcsPath); resolve a
    //    human-clickable signed GET URL for the order from GET /uploads.
    let displayUrl = '';
    const gcsRef = signed.gcsPath || '';
    try {
      const listing = await api('/uploads', { method: 'GET' });
      const match = (listing && listing.uploads || []).find((u) => u.gcsPath === signed.gcsPath);
      if (match) displayUrl = match.url;
    } catch (_e) {
      // GUARD-OK: the upload ALREADY SUCCEEDED — this listing call only fetches a
      // thumbnail URL for the preview. Losing it costs a picture, never the
      // order: `gcsPath` is what links the file to the line item.
      console.debug('[studio] upload preview URL unavailable', _e);
    }
    return { gcsRef, displayUrl, fileName: file.name };
  }

  // Stamp the order form so the photo rides cart → checkout → order → fulfillment.
  function stamp(gcsRef, displayUrl) {
    if (refInput) refInput.value = gcsRef || '';
    if (urlInput) urlInput.value = displayUrl || '';
  }

  async function handleFile(file) {
    if (!file || busy) return;
    if (!ALLOWED_TYPES.has(file.type)) { emitError('Use a JPG, PNG or WebP image.'); return; }
    if (file.size > MAX_BYTES) { emitError(file.name + ' is over the 15 MB limit.'); return; }
    if (!auth.currentUser) {
      emitError('Sign in to attach your clothing photo.');
      return;
    }
    busy = true;
    try {
      const { gcsRef, displayUrl, fileName } = await uploadOne(file);
      stamp(gcsRef, displayUrl);
      setStatus('');
      emitDone({ displayUrl, ref: gcsRef, fileName });
    } catch (e) {
      if (e.message === 'not-signed-in') emitError('Sign in to attach your clothing photo.');
      else emitError(e.message, e.status);
    } finally {
      busy = false;
    }
  }

  root.addEventListener('composer:upload-file', (e) => {
    const file = e.detail && e.detail.file;
    handleFile(file);
  });
}
